import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import DVLTooltip, { DVLTooltipWpr } from './components/DVLTooltip';
import startOverIcon from '../src/assets/images/start-over.svg';
import './styles/main.scss';
// Initialize Highcharts accessibility module
try {
    const accessibilityModule = require('highcharts/modules/accessibility');
    if (typeof accessibilityModule === 'function') {
        accessibilityModule(Highcharts);
    }
    else if (accessibilityModule.default && typeof accessibilityModule.default === 'function') {
        accessibilityModule.default(Highcharts);
    }
}
catch (e) {
    console.warn('Could not load Highcharts accessibility module:', e);
}
const App = () => {
    const { t, i18n } = useTranslation();
    const [formData, setFormData] = useState({
        initialContribution: '2000',
        contributionFrequency: 'monthly',
        contributionAmount: '200',
        savingsPeriod: '5',
        interestRate: '1',
    });
    const [result, setResult] = useState(null);
    const [displayedResult, setDisplayedResult] = useState(null);
    const [displayedSavingsPeriod, setDisplayedSavingsPeriod] = useState('5');
    const [showResults, setShowResults] = useState(false);
    const [focusTarget, setFocusTarget] = useState(null);
    const savingsHeadingRef = useRef(null);
    const formHeadingRef = useRef(null);
    // Display values with formatting
    const [displayValues, setDisplayValues] = useState({
        initialContribution: '2,000',
        contributionAmount: '200',
        interestRate: '1',
    });
    // Handle focus management after render
    useEffect(() => {
        if (focusTarget === 'results' && savingsHeadingRef.current) {
            savingsHeadingRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            setTimeout(() => {
                if (savingsHeadingRef.current) {
                    savingsHeadingRef.current.focus({ preventScroll: true });
                }
                setFocusTarget(null);
            }, 150);
        }
        else if (focusTarget === 'form' && formHeadingRef.current) {
            formHeadingRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            setTimeout(() => {
                if (formHeadingRef.current) {
                    formHeadingRef.current.focus({ preventScroll: true });
                }
                setFocusTarget(null);
            }, 150);
        }
    }, [focusTarget, showResults]);
    const taggingForInput = useCallback((name, value = 0) => {
        // handle tagging for each inputs
        let obj = {
            "event": "click",
            "pageIndex": 0,
            "details": "",
            "element_type": "other"
        };
        switch (name) {
            case 'initialContribution':
                obj.details = `savings calculator - initial contribution - ${value}`;
                break;
            case 'contributionFrequency':
                obj.details = `savings calculator - contribution frequency - ${value}`;
                break;
            case 'contributionAmount':
                obj.details = `savings calculator - how much to contribute each time - ${value}`;
                break;
            case 'savingsPeriod':
                obj.details = `savings calculator - length of time to contribute - ${value}`;
                break;
            case 'interestRate':
                obj.details = `savings calculator - expected interest rate - ${value}`;
                break;
            default:
                break;
        }
        // console.log("Tagging Object: ", obj);
        if (obj.details !== "") {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(obj);
        }
    }, []);
    const formatNumber = (value, isPercentage = false) => {
        // Remove all non-digit characters except decimal point
        const cleanValue = value.replace(/[^0-9.]/g, '');
        if (!cleanValue || cleanValue === '.')
            return '';
        const parts = cleanValue.split('.');
        const integerPart = parts[0];
        const decimalPart = parts[1];
        // Format with locale-specific thousand separators
        const locale = i18n.language === 'fr' ? 'fr-CA' : 'en-US';
        const formatted = parseInt(integerPart || '0').toLocaleString(locale);
        if (decimalPart !== undefined) {
            const separator = locale === 'fr-CA' ? ',' : '.';
            return formatted + separator + decimalPart;
        }
        return formatted;
    };
    const parseFormattedNumber = (value) => {
        // Remove all formatting and keep only digits and decimal point
        const locale = i18n.language === 'fr' ? 'fr-CA' : 'en-US';
        const decimalSeparator = locale === 'fr-CA' ? ',' : '.';
        // Replace locale-specific decimal separator with standard period
        let cleaned = value.replace(new RegExp(`\\${decimalSeparator}`, 'g'), '.');
        // Remove thousand separators and any other non-digit/non-period characters
        cleaned = cleaned.replace(/[^0-9.]/g, '');
        return cleaned;
    };
    const handleInputOnBlur = useCallback((e) => {
        const { name, value } = e.target;
        taggingForInput(name, value);
    }, []);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'contributionFrequency' || name === 'savingsPeriod') {
            taggingForInput(name, value);
            setFormData({
                ...formData,
                [name]: value,
            });
            return;
        }
        // Handle currency and percentage fields
        const cleanValue = parseFormattedNumber(value);
        const isPercentage = name === 'interestRate';
        // Apply limits and handle empty values
        let limitedValue = cleanValue;
        const numValue = parseFloat(cleanValue);
        // If field is empty or invalid, set to minimum allowed value
        if (!cleanValue || cleanValue === '' || isNaN(numValue)) {
            limitedValue = '0';
        }
        else if (name === 'initialContribution' && numValue > 100000) {
            limitedValue = '100000';
        }
        else if (name === 'contributionAmount' && numValue > 5000) {
            limitedValue = '5000';
        }
        else if (name === 'interestRate' && numValue > 5) {
            limitedValue = '5';
        }
        else if (numValue < 0) {
            // Ensure no negative values
            limitedValue = '0';
        }
        setFormData({
            ...formData,
            [name]: limitedValue,
        });
        // Update display value with formatting
        if (name === 'initialContribution' || name === 'contributionAmount' || name === 'interestRate') {
            setDisplayValues({
                ...displayValues,
                [name]: formatNumber(limitedValue, isPercentage),
            });
        }
    };
    // Core calculation logic shared by all handlers
    const performCalculation = () => {
        const initial = parseFloat(formData.initialContribution) || 0;
        const contribution = parseFloat(formData.contributionAmount) || 0;
        const years = parseInt(formData.savingsPeriod) || 0;
        const rate = parseFloat(formData.interestRate) / 100;
        const periodsPerYear = formData.contributionFrequency === 'weekly' ? 52 :
            formData.contributionFrequency === 'bi-weekly' ? 26 :
                formData.contributionFrequency === 'monthly' ? 12 : 1;
        let totalDeposits = initial;
        let totalValue = initial;
        const yearlyData = [];
        for (let year = 1; year <= years; year++) {
            for (let period = 0; period < periodsPerYear; period++) {
                totalValue = totalValue * (1 + rate / periodsPerYear);
                totalValue += contribution;
                totalDeposits += contribution;
            }
            const interest = totalValue - totalDeposits;
            yearlyData.push({
                year,
                deposits: totalDeposits,
                interest,
            });
        }
        const totalInterestEarned = totalValue - totalDeposits;
        const calculatedResult = {
            totalValue,
            totalDeposits,
            totalInterestEarned,
            yearlyData,
        };
        setResult(calculatedResult);
        setDisplayedResult(calculatedResult);
        setDisplayedSavingsPeriod(formData.savingsPeriod);
        return calculatedResult;
    };
    // Real-time calculation on form changes
    useEffect(() => {
        performCalculation();
    }, [formData]);
    const handleCalculate = () => {
        performCalculation();
        // On mobile, switch to results view
        setShowResults(true);
        // Focus the results heading
        setFocusTarget('results');
    };
    const handleStartOver = () => {
        // Reset form to defaults
        setFormData({
            initialContribution: '2000',
            contributionFrequency: 'monthly',
            contributionAmount: '200',
            savingsPeriod: '5',
            interestRate: '1',
        });
        setDisplayValues({
            initialContribution: '2,000',
            contributionAmount: '200',
            interestRate: '1',
        });
        // On mobile, switch back to form view
        setShowResults(false);
        // Focus the form heading
        setFocusTarget('form');
    };
    const formatCurrency = (value) => {
        const locale = i18n.language === 'fr' ? 'fr-CA' : 'en-US';
        const formatted = Math.round(value).toLocaleString(locale);
        return i18n.language === 'fr' ? formatted + ' $' : '$' + formatted;
    };
    const chartOptions = displayedResult
        ? {
            chart: {
                type: 'column',
                height: window.innerWidth <= 768 ? 365 : 570,
                reflow: true,
                animation: {
                    duration: 1000,
                },
            },
            title: {
                text: '',
            },
            legend: {
                enabled: false,
            },
            xAxis: {
                categories: displayedResult.yearlyData.map((d) => i18n.language === 'fr'
                    ? `Année ${d.year}`
                    : `Year ${d.year}`),
                title: {
                    text: `<span class="offscreen">${i18n.language === 'fr' ? 'Années' : 'Years'}</span>`,
                    useHTML: true,
                },
                labels: {
                    style: {
                        color: '#485465',
                        fontFamily: 'Roboto',
                        fontSize: window.innerWidth <= 768 ? '12px' : '14.5px',
                        fontWeight: '300',
                        letterSpacing: '0.175px',
                    },
                },
            },
            yAxis: {
                min: 0,
                title: {
                    text: '<span class="offscreen">Amount ($)</span>',
                    useHTML: true,
                },
                labels: {
                    style: {
                        color: '#485465',
                        fontFamily: 'Roboto',
                        fontSize: window.innerWidth <= 768 ? '12px' : '14.5px',
                        fontWeight: '300',
                        letterSpacing: '0.175px',
                    },
                    formatter: function () {
                        const locale = i18n.language === 'fr' ? 'fr-CA' : 'en-US';
                        const formatted = Math.round(this.value).toLocaleString(locale);
                        return i18n.language === 'fr' ? formatted + ' $' : '$' + formatted;
                    },
                },
            },
            tooltip: {
                style: {
                    fontSize: '16px',
                    fontWeight: '400',
                },
                pointFormatter: function () {
                    const locale = i18n.language === 'fr' ? 'fr-CA' : 'en-US';
                    const formatted = Math.round(this.y).toLocaleString(locale);
                    const currency = i18n.language === 'fr' ? formatted + ' $' : '$' + formatted;
                    return '<span style="color:' + this.color + '">●</span> ' + this.series.name + ': <b>' + currency + '</b><br/>';
                },
            },
            plotOptions: {
                column: {
                    stacking: 'normal',
                    borderRadius: 0,
                    pointPadding: 0.1,
                    groupPadding: 0.05,
                    borderWidth: 0,
                },
            },
            accessibility: {
                enabled: true,
                description: 'Compound interest calculator chart showing deposits and interest earned over time',
            },
            series: [
                {
                    name: t('totalInterestEarned'),
                    data: displayedResult.yearlyData.map((d) => d.interest),
                    type: 'column',
                    color: '#F7C33A',
                },
                {
                    name: t('totalDeposits'),
                    data: displayedResult.yearlyData.map((d) => d.deposits),
                    type: 'column',
                    color: '#2A609E',
                },
            ],
        }
        : {};
    return (React.createElement("div", { className: "compound-interest-calculator" },
        React.createElement("div", { className: "calculator-container" },
            React.createElement("div", { className: `calculator-form ${showResults ? 'mobile-hidden' : ''}` },
                React.createElement("div", null,
                    React.createElement("h2", { className: "what-saving-for", ref: formHeadingRef, tabIndex: -1 }, t('whatAreYouSavingFor')),
                    React.createElement("p", null, t('savingsGoalDescription'))),
                React.createElement("div", { className: "divider mar-tb-0" }),
                React.createElement("div", null,
                    React.createElement("label", { htmlFor: "initialContribution", style: { display: 'block' } },
                        React.createElement(DVLTooltipWpr, null,
                            t('initialContribution'),
                            React.createElement(DVLTooltip, { id: "initial-contribution-tooltip", lang: i18n.language, dataH: "rightedge", dataV: "bottom" },
                                React.createElement("p", null, t('initialContributionTooltip'))))),
                    React.createElement("p", { className: "label-subtext" }, t('initialContributionSubtext')),
                    React.createElement("div", { className: `input-with-symbol ${i18n.language === 'fr' ? 'symbol-after' : 'symbol-before'}` },
                        React.createElement("span", { className: "currency-symbol", "aria-hidden": "true" }, "$"),
                        React.createElement("input", { type: "text", id: "initialContribution", name: "initialContribution", value: displayValues.initialContribution, onChange: handleInputChange, onBlur: handleInputOnBlur, inputMode: "decimal", "aria-label": t('initialContribution') }))),
                React.createElement("div", { className: "select-wpr mar-b-0" },
                    React.createElement("label", { htmlFor: "contributionFrequency" }, t('contributionFrequency')),
                    React.createElement("p", { className: "label-subtext" }, t('contributionFrequencySubtext')),
                    React.createElement("select", { name: "contributionFrequency", id: "contributionFrequency", value: formData.contributionFrequency, onChange: handleInputChange },
                        React.createElement("option", { value: "weekly" }, t('weekly')),
                        React.createElement("option", { value: "bi-weekly" }, t('biWeekly')),
                        React.createElement("option", { value: "monthly" }, t('monthly')),
                        React.createElement("option", { value: "annually" }, t('annually')))),
                React.createElement("div", null,
                    React.createElement("label", { htmlFor: "contributionAmount", style: { display: 'block' } },
                        React.createElement(DVLTooltipWpr, null,
                            t('contributionAmount'),
                            React.createElement(DVLTooltip, { id: "contribution-amount-tooltip", lang: i18n.language, dataH: "rightedge", dataV: "bottom" },
                                React.createElement("p", null, t('contributionAmountTooltip'))))),
                    React.createElement("p", { className: "label-subtext" }, t('contributionAmountSubtext')),
                    React.createElement("div", { className: `input-with-symbol ${i18n.language === 'fr' ? 'symbol-after' : 'symbol-before'}` },
                        React.createElement("span", { className: "currency-symbol", "aria-hidden": "true" }, "$"),
                        React.createElement("input", { type: "text", id: "contributionAmount", name: "contributionAmount", value: displayValues.contributionAmount, onChange: handleInputChange, onBlur: handleInputOnBlur, inputMode: "decimal", "aria-label": t('contributionAmount') }))),
                React.createElement("div", { className: "select-wpr mar-b-0" },
                    React.createElement("label", { htmlFor: "savingsPeriod" }, t('savingsPeriod')),
                    React.createElement("p", { className: "label-subtext" }, t('savingsPeriodSubtext')),
                    React.createElement("select", { name: "savingsPeriod", id: "savingsPeriod", value: formData.savingsPeriod, onChange: handleInputChange },
                        React.createElement("option", { value: "1" },
                            "1 ",
                            t('yearDropdown')),
                        React.createElement("option", { value: "2" },
                            "2 ",
                            t('yearsDropdown')),
                        React.createElement("option", { value: "3" },
                            "3 ",
                            t('yearsDropdown')),
                        React.createElement("option", { value: "4" },
                            "4 ",
                            t('yearsDropdown')),
                        React.createElement("option", { value: "5" },
                            "5 ",
                            t('yearsDropdown')),
                        React.createElement("option", { value: "10" },
                            "10 ",
                            t('yearsDropdown')))),
                React.createElement("div", null,
                    React.createElement("label", { htmlFor: "interestRate", style: { display: 'block' } }, t('interestRate')),
                    React.createElement("p", { className: "label-subtext" }, t('interestRateSubtext')),
                    React.createElement("div", { className: "input-with-symbol symbol-after" },
                        React.createElement("input", { type: "text", id: "interestRate", name: "interestRate", value: displayValues.interestRate, onChange: handleInputChange, onBlur: handleInputOnBlur, inputMode: "decimal", "aria-label": t('interestRate') }),
                        React.createElement("span", { className: "currency-symbol", "aria-hidden": "true" }, "%")),
                    React.createElement("a", { href: i18n.language === 'fr'
                            ? 'https://www.rbcroyalbank.com/fr/taux/persacct.html'
                            : 'https://www.rbcroyalbank.com/rates/persacct.html', target: "_blank", rel: "noopener noreferrer", className: "standalone-link-reverse learnMoreRatesBtn" }, t('learnMoreSavingsInterestRates'))),
                React.createElement("div", { className: "divider mar-tb-0" }),
                React.createElement("div", { className: "form-btn-wpr" },
                    React.createElement("button", { className: `btn-start-over desktop-hidden ${showResults ? 'mobile-visible' : 'mobile-hidden'}`, onClick: handleStartOver },
                        React.createElement("img", { src: startOverIcon, alt: "", className: "btn-icon" }),
                        t('startOver')),
                    React.createElement("button", { className: "btn-calculate mob-mar-r-0 desktop-hidden", onClick: handleCalculate }, t('calculate')))),
            displayedResult && (React.createElement("div", { className: `calculator-results ${showResults ? 'mobile-visible' : ''}` },
                React.createElement("div", { className: "could-save-section" },
                    React.createElement("h3", { className: "could-save-heading", ref: savingsHeadingRef, tabIndex: -1 },
                        React.createElement("span", { className: "inline-block" }, t('youCouldSave')),
                        React.createElement("p", { className: "could-save-heading-amt" },
                            formatCurrency(displayedResult.totalValue),
                            React.createElement("sup", null,
                                React.createElement("a", { href: "javascript:void(0);", "data-target": "#legal-dagger", className: "scrollto" },
                                    React.createElement("span", { className: "offscreen" }, "legal bug "),
                                    "\u2020"))),
                        React.createElement("span", { className: "inline-block" },
                            "    ",
                            t('overTheNext'),
                            " ",
                            displayedSavingsPeriod,
                            " ",
                            parseInt(displayedSavingsPeriod) === 1 ? t('year') : t('years')))),
                React.createElement("div", { className: "chart-container" },
                    React.createElement(HighchartsReact, { highcharts: Highcharts, options: chartOptions, containerProps: { style: { width: '100%', height: '100%' } } })),
                React.createElement("div", { className: "result-summary" },
                    React.createElement("div", { className: "legend-item mar-b-qtr" },
                        React.createElement("div", { className: "legend-label" },
                            React.createElement("span", { className: "legend-icon", style: { backgroundColor: '#2A609E' } }),
                            React.createElement("span", { className: "legend-text" }, t('totalDeposits'))),
                        React.createElement("span", { className: "legend-value" }, formatCurrency(displayedResult.totalDeposits))),
                    React.createElement("div", { className: "legend-item" },
                        React.createElement("div", { className: "legend-label" },
                            React.createElement("span", { className: "legend-icon", style: { backgroundColor: '#F7C33A' } }),
                            React.createElement("span", { className: "legend-text" }, t('totalInterestEarned'))),
                        React.createElement("span", { className: "legend-value" }, formatCurrency(displayedResult.totalInterestEarned))),
                    React.createElement("div", { className: "divider" }),
                    React.createElement("div", { className: "legend-item total-value" },
                        React.createElement("div", { className: "legend-label" },
                            React.createElement("span", { className: "legend-text" }, t('totalValue'))),
                        React.createElement("span", { className: "legend-value" }, formatCurrency(displayedResult.totalValue)))),
                React.createElement("div", { className: "w-100" },
                    React.createElement("div", { className: "w-100" },
                        React.createElement("button", { className: "mobile-back-button", onClick: handleStartOver },
                            React.createElement("img", { src: startOverIcon, alt: "", className: "btn-icon" }),
                            t('startOver'))),
                    React.createElement("div", { className: "cta-section" },
                        React.createElement("p", { className: "cta-heading" }, t('readyToStartSaving')),
                        React.createElement("a", { href: i18n.language === 'fr'
                                ? 'https://www.rbcroyalbank.com/fr/comptes-bancaires/comptes-depargne/index.html'
                                : 'https://www.rbcroyalbank.com/bank-accounts/savings-accounts/index.html', className: "btn primary mar-t-0" }, t('readyToStartSavingCta')))))))));
};
export default App;
